import { useMemo } from 'react';

import useAuth from './useAuth';

/** Browser fallback when the user hasn't set a personal timezone yet. */
function browserZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** IANA id from a stored label ("Asia/Kolkata (IST)" -> "Asia/Kolkata"). */
function ianaOf(label) {
  return label ? label.split(' ')[0] : browserZone();
}

/**
 * useTimeZone — the viewer's own timezone, used to render class times so each
 * person sees sessions in *their* local time (a student in New York and a
 * mentor in London see the same instant as different wall-clock times).
 *
 * Resolves from the user's profile `timezone` (set in Settings / seeded at
 * sign-up), falling back to the browser zone for signed-out or not-yet-set
 * cases.
 *
 * @returns {{ label: string, zone: string, abbrev: string }}
 *   label  — stored label, e.g. "Asia/Kolkata (IST)"
 *   zone   — IANA id, e.g. "Asia/Kolkata" (pass to the date formatters)
 *   abbrev — short code, e.g. "IST" (for display)
 */
export default function useTimeZone() {
  const { profile } = useAuth();
  const label = profile?.timezone || '';

  return useMemo(() => {
    const zone = ianaOf(label);
    const m = label.match(/\(([^)]+)\)/);
    return { label: label || zone, zone, abbrev: m ? m[1] : '' };
  }, [label]);
}
